// src/components/chat/RequestList.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, List, ListItem, Typography, IconButton,
  Tooltip, Chip, Button, CircularProgress, Alert, Divider, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, Avatar, TextField,
  MenuItem, Select, InputLabel, FormControl,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BrushIcon from '@mui/icons-material/Brush';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { getJSON, postJSON, patchJSON } from '../../utils/api';
import '../../styles/RequestList.css';

const ROLE_LABELS = {
  edit:      { label: 'Editor',   icon: <EditIcon fontSize="inherit" />,       color: '#16a34a' },
  design:    { label: 'Designer', icon: <BrushIcon fontSize="inherit" />,      color: '#8b5cf6' },
  view_only: { label: 'Viewer',   icon: <VisibilityIcon fontSize="inherit" />, color: '#0ea5e9' },
};

const RECEIVED_STATUS = {
  pending:  { label: 'Pending',  color: 'warning' },
  accepted: { label: 'Accepted', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
  expired:  { label: 'Expired',  color: 'error' },
};

const SENT_STATUS = {
  pending:  { label: 'Waiting for response', color: '#f59e0b', bg: '#fef3c7' },
  accepted: { label: 'Accepted',             color: '#16a34a', bg: '#dcfce7' },
  rejected: { label: 'Rejected',             color: '#ef4444', bg: '#fee2e2' },
  expired:  { label: 'Expired',              color: '#ef4444', bg: '#fee2e2' },
};

const STATUS_STYLE = {
  pending:  { bg: '#f59e0b', text: '#fff' },
  accepted: { bg: '#16a34a', text: '#fff' },
  rejected: { bg: '#ef4444', text: '#fff' },
  expired:  { bg: '#ef4444', text: '#fff' },
};

const AVATAR_COLORS = ['#7c3aed', '#D4AF37', '#16a34a', '#0ea5e9', '#ef4444', '#413B8D'];

function avatarBg(i) { return AVATAR_COLORS[i % AVATAR_COLORS.length]; }

function formatDate(d) {
  if (!d) return null;
  const date = new Date(d);
  return isNaN(date.getTime()) ? null : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isExpired(d) {
  if (!d) return false;
  return new Date(d) < new Date();
}

function isOverdue(d) {
  if (!d) return false;
  return new Date(d) < new Date();
}

function effectiveStatus(inv) {
  return inv.status === 'pending' && isExpired(inv.expirationDate) ? 'expired' : inv.status;
}

// ── Update Request Dialog ───────────────────────────────────────────────────

function toDateInput(isoStr) {
  if (!isoStr) return '';
  return isoStr.split('T')[0];
}

function UpdateRequestDialog({ inv, open, onClose, onSaved, mode = 'update' }) {
  const [message, setMessage] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isCreate = mode === 'create';
  const isCoverOffer = inv?.type === 'cover_offer';
  // A manuscript goal assignment is handled exactly like a cover task here.
  const isCoverTask = inv?.type === 'cover_task' || inv?.type === 'manuscript_goal';
  const isCoverVersion = inv?.type === 'cover_version';
  const showRole = !isCoverOffer && !isCoverTask;

  useEffect(() => {
    if (open && inv) {
      setMessage(inv.taskDetail || inv.message || '');
      // A recreated request needs fresh dates — the old ones already lapsed.
      setDueDate(isCreate ? '' : toDateInput(inv.dueDate));
      setExpirationDate(isCreate ? '' : toDateInput(inv.expirationDate));
      setRole(inv.role || 'edit');
      setError(null);
    }
  }, [open, inv, isCreate]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isCreate) {
        const body = {
          type: inv.type,
          draftId: inv.draftId,
          toUserId: inv.toUserId,
          message,
          dueDate: dueDate || null,
          expirationDate: expirationDate || null,
        };
        if (inv.versionId) body.versionId = inv.versionId;
        if (showRole) body.role = role;
        if (isCoverOffer && inv.price != null) body.price = inv.price;

        const res = await postJSON('/api/invitations', body);
        onSaved(res.invitation);
      } else {
        const body = {
          dueDate: dueDate || null,
          expirationDate: expirationDate || null,
        };
        if (isCoverTask) body.taskDetail = message;
        else body.message = message;
        if (showRole) body.role = role;

        const updated = await patchJSON(`/api/invitations/${inv.id}`, body);
        onSaved(updated);
      }
      onClose();
    } catch (e) {
      setError(e.message || (isCreate ? 'Failed to create request' : 'Failed to update'));
    } finally {
      setSaving(false);
    }
  };

  if (!inv) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ bgcolor: '#413B8D', color: '#fff', fontWeight: 700 }}>
        {isCreate ? 'Create New Request' : 'Update Request'}
      </DialogTitle>
      <DialogContent sx={{ pt: '1.5rem !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="Assignment Detail"
          multiline
          minRows={3}
          fullWidth
          value={message}
          onChange={e => setMessage(e.target.value)}
          size="small"
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Due Date"
            type="date"
            size="small"
            fullWidth
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Request Expiration Date"
            type="date"
            size="small"
            fullWidth
            value={expirationDate}
            onChange={e => setExpirationDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {showRole && (
          <FormControl size="small" fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={role} label="Role" onChange={e => setRole(e.target.value)}>
              {!isCoverVersion && <MenuItem value="edit">Editor</MenuItem>}
              {isCoverVersion && <MenuItem value="design">Designer</MenuItem>}
              <MenuItem value="view_only">Viewer</MenuItem>
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#6b7280' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#413B8D', '&:hover': { bgcolor: '#5A52A3' } }}
        >
          {isCreate ? 'Send Request' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Invitation Detail Modal ─────────────────────────────────────────────────

// mode: 'received' shows Accept/Reject; 'sent' is view-only with comments
function InvitationDetailModal({ inv, open, onClose, onAccept, onReject, onAcceptCoverOffer, onCancel, onUpdate, onCreated, mode = 'received' }) {
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // 'accept' | 'reject' | 'cancel'
  const [actionError, setActionError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const commentsEndRef = useRef(null);

  useEffect(() => {
    if (open && inv?.id) {
      setComments([]);
      setActionError(null);
      setCommentText('');
      loadComments();
    }
  }, [open, inv?.id]); // eslint-disable-line

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  const loadComments = async () => {
    setCommentsLoading(true);
    try {
      const data = await getJSON(`/api/invitations/${inv.id}/comments`);
      setComments(data || []);
    } catch { /* silent */ }
    finally { setCommentsLoading(false); }
  };

  const handleSend = async () => {
    const text = commentText.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const comment = await postJSON(`/api/invitations/${inv.id}/comments`, { text });
      setComments(prev => [...prev, comment]);
      setCommentText('');
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  const handleAccept = async () => {
    setActionLoading('accept');
    setActionError(null);
    try {
      if (inv.type === 'cover_offer') {
        onClose();
        await onAcceptCoverOffer(inv);
      } else {
        await onAccept(inv.id);
        onClose();
      }
    } catch (e) {
      setActionError(e.message || 'Action failed');
    } finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    setActionLoading('reject');
    setActionError(null);
    try {
      await onReject(inv.id);
      onClose();
    } catch (e) {
      setActionError(e.message || 'Action failed');
    } finally { setActionLoading(null); }
  };

  const handleCancel = async () => {
    setActionLoading('cancel');
    setActionError(null);
    try {
      await onCancel(inv.id);
      onClose();
    } catch (e) {
      setActionError(e.message || 'Action failed');
    } finally { setActionLoading(null); }
  };

  if (!inv) return null;

  const eff = effectiveStatus(inv);
  const isPending = eff === 'pending';
  const isCoverOffer = inv.type === 'cover_offer';
  // A manuscript goal assignment is handled exactly like a cover task here.
  const isCoverTask = inv.type === 'cover_task' || inv.type === 'manuscript_goal';
  const expired = eff === 'expired';
  const overdue = isPending && isOverdue(inv.dueDate);
  const canRecreate = inv.type === 'draft' || inv.type === 'cover_version' || inv.type === 'cover_offer';

  const title = isCoverTask && inv.taskName ? inv.taskName : (inv.draftTitle || 'Untitled');
  const typeLabel = inv.type === 'cover_task' ? 'Task Assignment'
    : inv.type === 'manuscript_goal' ? 'Goal Assignment'
    : inv.type === 'cover_version' ? 'Cover Design'
    : inv.type === 'cover_offer' ? 'Cover Offer' : 'Manuscript';

  const assignees = (isCoverTask && inv.assigneeUsernames?.length > 0)
    ? inv.assigneeUsernames
    : [inv.toUsername].filter(Boolean);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          height: { xs: '95vh', sm: '78vh' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: '#413B8D', px: 3, py: 2.5, color: '#fff', flexShrink: 0, position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 4 }}>
          <Typography variant="h5" fontWeight={700} sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}>
            {title}
          </Typography>
          <Chip
            size="small"
            label={typeLabel}
            sx={{
              fontSize: '0.68rem', height: 20, flexShrink: 0,
              bgcolor: 'rgba(255,255,255,0.15)', color: '#fff',
              ...(isCoverTask ? { bgcolor: 'rgba(212,175,55,0.25)', color: '#D4AF37' } : {}),
            }}
          />
          {expired && (
            <Chip size="small" label="Expired" sx={{ fontSize: '0.68rem', height: 20, flexShrink: 0, bgcolor: 'rgba(239,68,68,0.25)', color: '#fca5a5' }} />
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute', top: 10, right: 10,
            color: 'rgba(255,255,255,0.7)',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* Left — request details */}
        <Box sx={{
          flex: '1.2 1 0',
          p: 3,
          overflowY: 'auto',
          borderRight: '1px solid #eef2f5',
        }}>
          {/* Row 1: Status | Requestor | Expiration Date — always visible */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" fontWeight={700}>Status:</Typography>
              <Box sx={{
                bgcolor: STATUS_STYLE[effectiveStatus(inv)]?.bg || '#9ca3af',
                color: STATUS_STYLE[effectiveStatus(inv)]?.text || '#fff',
                px: 1.5, py: 0.25, borderRadius: 999,
                fontSize: '0.78rem', fontWeight: 600, lineHeight: 1.8,
              }}>
                {RECEIVED_STATUS[effectiveStatus(inv)]?.label || inv.status}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" fontWeight={700}>Requestor:</Typography>
              <Tooltip title={inv.fromUsername || '?'}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: '#7c3aed', fontSize: '0.72rem', cursor: 'default' }}>
                  {(inv.fromUsername || '?')[0].toUpperCase()}
                </Avatar>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" fontWeight={700}>Request Expiration Date:</Typography>
              <Typography variant="body2" color={inv.expirationDate ? 'text.primary' : 'text.disabled'}>
                {formatDate(inv.expirationDate) || '—'}
              </Typography>
            </Box>
          </Box>

          {/* Row 2: Due Date | Name of Document — always visible */}
          <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Typography variant="body2" fontWeight={700}>Due Date:</Typography>
              <Typography variant="body2" color={overdue ? '#ef4444' : inv.dueDate ? 'text.primary' : 'text.disabled'} fontWeight={overdue ? 700 : 400}>
                {formatDate(inv.dueDate) || '—'}
              </Typography>
              {overdue && (
                <Chip size="small" label="Overdue" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#fee2e2', color: '#ef4444', fontWeight: 700 }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <Typography variant="body2" fontWeight={700}>Book:</Typography>
              <Typography variant="body2">{inv.draftTitle || '—'}</Typography>
            </Box>
            {inv.versionName && (
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Typography variant="body2" fontWeight={700}>Version:</Typography>
                <Typography variant="body2">{inv.versionName}</Typography>
              </Box>
            )}
          </Box>

          {/* Assigned to — always visible */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={700} gutterBottom>Assigned to:</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {assignees.length > 0 ? assignees.map((username, i) => (
                <Tooltip key={i} title={username || '?'}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: avatarBg(i), fontSize: '0.8rem', cursor: 'default' }}>
                    {(username || '?')[0].toUpperCase()}
                  </Avatar>
                </Tooltip>
              )) : (
                <Typography variant="body2" color="text.disabled">—</Typography>
              )}
            </Box>
          </Box>

          {/* Role (non-offer, non-task) */}
          {!isCoverOffer && !isCoverTask && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" fontWeight={700}>Role:</Typography>
              {inv.role ? (
                <Chip
                  size="small"
                  icon={ROLE_LABELS[inv.role]?.icon}
                  label={ROLE_LABELS[inv.role]?.label || inv.role}
                  sx={{ fontSize: '0.7rem', height: 22, color: ROLE_LABELS[inv.role]?.color, borderColor: ROLE_LABELS[inv.role]?.color }}
                  variant="outlined"
                  onClick={() => {}}
                />
              ) : (
                <Typography variant="body2" color="text.disabled">—</Typography>
              )}
            </Box>
          )}

          {/* Cover offer price */}
          {isCoverOffer && (
            <Box sx={{ mb: 2, display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Typography variant="body2" fontWeight={700}>Price:</Typography>
              <Typography variant="body2" sx={{ color: inv.price != null ? '#059669' : undefined, fontWeight: 700 }}>
                {inv.price != null ? `$${Number(inv.price).toFixed(2)}` : '—'}
              </Typography>
            </Box>
          )}

          {/* Task name chip (cover_task) */}
          {isCoverTask && (
            <Box sx={{ mb: 2, display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Typography variant="body2" fontWeight={700}>Task:</Typography>
              {inv.taskName ? (
                <Chip
                  size="small"
                  icon={<AssignmentIcon fontSize="inherit" />}
                  label={inv.taskName}
                  sx={{ fontSize: '0.7rem', height: 22, color: '#D4AF37', borderColor: '#D4AF37' }}
                  variant="outlined"
                />
              ) : (
                <Typography variant="body2" color="text.disabled">—</Typography>
              )}
            </Box>
          )}

          {/* Assignment Detail — always visible */}
          <Box>
            <Typography variant="body2" fontWeight={700} gutterBottom>Assignment Detail:</Typography>
            {(inv.taskDetail || inv.message) ? (
              <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1, px: 2, py: 1.5, borderLeft: '3px solid #413B8D' }}>
                <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {inv.taskDetail || inv.message}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.disabled">—</Typography>
            )}
          </Box>

          {/* Response date */}
          {inv.respondedAt && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              {inv.status === 'accepted' ? 'Accepted' : 'Rejected'} on {formatDate(inv.respondedAt)}
            </Typography>
          )}
        </Box>

        {/* Right — comment history */}
        <Box sx={{
          flex: '1 1 0',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#faf8ff',
          minHeight: 0,
        }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
            Comment History
          </Typography>

          {/* Comments list */}
          <Box sx={{ flex: 1, overflowY: 'auto', mb: 1.5 }}>
            {commentsLoading ? (
              <Box display="flex" justifyContent="center" pt={3}>
                <CircularProgress size={22} />
              </Box>
            ) : comments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ pt: 3, fontStyle: 'italic' }}>
                No comments yet.
              </Typography>
            ) : (
              comments.map(c => (
                <Box key={c.id} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: c.isMine ? '#413B8D' : '#111827' }}>
                      {c.isMine ? 'Me' : c.username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(c.createdAt).toLocaleString('en-US', {
                        month: 'short', day: 'numeric',
                        hour: 'numeric', minute: '2-digit',
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{
                    bgcolor: c.isMine ? 'rgba(65,59,141,0.05)' : '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 1.5, px: 1.5, py: 1,
                  }}>
                    <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.5 }}>
                      {c.text}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
            <div ref={commentsEndRef} />
          </Box>

          {/* Comment input */}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <TextField
              multiline
              maxRows={3}
              placeholder="Start type here..."
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2, bgcolor: '#fff', fontSize: '0.85rem',
                  '& fieldset': { borderColor: '#d1d5db' },
                  '&:hover fieldset': { borderColor: '#413B8D' },
                  '&.Mui-focused fieldset': { borderColor: '#413B8D' },
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!commentText.trim() || sending}
              sx={{
                bgcolor: '#413B8D', color: '#fff', borderRadius: 2,
                width: 36, height: 36, alignSelf: 'flex-end', flexShrink: 0,
                '&:hover': { bgcolor: '#5A52A3' },
                '&.Mui-disabled': { bgcolor: '#d1d5db', color: '#9ca3af' },
              }}
            >
              {sending
                ? <CircularProgress size={16} color="inherit" />
                : <SendIcon sx={{ fontSize: 18 }} />
              }
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Action footer */}
      <Box sx={{
        display: 'flex', gap: 1.5, px: 3, py: 2,
        borderTop: '1px solid #eef2f5', justifyContent: 'flex-end',
        flexShrink: 0, flexWrap: 'wrap',
      }}>
        {actionError && (
          <Typography variant="caption" color="error" sx={{ alignSelf: 'center', mr: 'auto' }}>
            {actionError}
          </Typography>
        )}
        {mode === 'received' && expired && (
          <Typography variant="caption" color="error" sx={{ alignSelf: 'center', mr: 'auto' }}>
            This invitation has expired and can no longer be accepted.
          </Typography>
        )}
        {mode === 'received' && isPending && !expired && (
          <>
            <Button
              variant="contained"
              onClick={handleAccept}
              disabled={!!actionLoading}
              startIcon={actionLoading === 'accept' ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#413B8D', '&:hover': { bgcolor: '#5A52A3' } }}
            >
              {isCoverOffer ? 'Accept & Assign to Draft' : 'Accept Request'}
            </Button>
            <Button
              variant="contained"
              onClick={handleReject}
              disabled={!!actionLoading}
              startIcon={actionLoading === 'reject' ? <CircularProgress size={14} color="inherit" /> : <CancelIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
            >
              Reject Request
            </Button>
          </>
        )}
        {mode === 'sent' && isPending && (
          <>
            <Button
              variant="contained"
              onClick={() => setEditOpen(true)}
              disabled={!!actionLoading}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#413B8D', '&:hover': { bgcolor: '#5A52A3' } }}
            >
              Update Request
            </Button>
            <Button
              variant="contained"
              onClick={handleCancel}
              disabled={!!actionLoading}
              startIcon={actionLoading === 'cancel' ? <CircularProgress size={14} color="inherit" /> : <CancelIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
            >
              Cancel Request
            </Button>
          </>
        )}
        {mode === 'sent' && expired && canRecreate && (
          <Button
            variant="contained"
            onClick={() => setCreateOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#413B8D', '&:hover': { bgcolor: '#5A52A3' } }}
          >
            Create New Request
          </Button>
        )}
        <Button
          onClick={onClose}
          variant="text"
          sx={{ textTransform: 'none', color: '#6b7280', '&:hover': { color: '#413B8D' } }}
        >
          Close
        </Button>
      </Box>

      <UpdateRequestDialog
        inv={inv}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={(updated) => { onUpdate?.(updated); }}
      />
      <UpdateRequestDialog
        inv={inv}
        mode="create"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={(created) => { onCreated?.(created); }}
      />
    </Dialog>
  );
}

// ── Invitation Card ─────────────────────────────────────────────────────────

function InvitationCard({ inv, mode, onAccept, onReject, onCancel, onAcceptCoverOffer, onClick }) {
  const [loading, setLoading] = useState(null); // 'accept' | 'reject' | 'cancel'
  const roleConfig = ROLE_LABELS[inv.role] || ROLE_LABELS.edit;
  const typeLabel  = inv.type === 'cover_task' ? 'Task Assignment'
    : inv.type === 'manuscript_goal' ? 'Goal Assignment'
    : inv.type === 'cover_version' ? 'Cover Design'
    : inv.type === 'cover_offer' ? 'Cover Offer' : 'Manuscript';
  const eff = effectiveStatus(inv);
  const isPending  = eff === 'pending';
  const isCoverOffer = inv.type === 'cover_offer';
  // A manuscript goal assignment is handled exactly like a cover task here.
  const isCoverTask = inv.type === 'cover_task' || inv.type === 'manuscript_goal';
  const expired = eff === 'expired';

  const act = async (action, fn, e) => {
    e.stopPropagation();
    setLoading(action);
    try { await fn(inv.id); } finally { setLoading(null); }
  };

  return (
    <ListItem
      className={`request-list-item ${isPending ? 'request-list-item--pending' : 'request-list-item--resolved'}`}
      onClick={onClick}
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1, py: 2, px: 2,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {/* Title row */}
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {inv.draftTitle || 'Untitled'}
          </Typography>
          <Chip size="small" label={typeLabel} variant="outlined"
            sx={{
              fontSize: '0.68rem', height: 20,
              ...(isCoverTask ? { borderColor: '#D4AF37', color: '#D4AF37' } : {}),
            }} />
          {inv.versionName && (
            <Typography variant="caption" color="text.secondary" noWrap>— {inv.versionName}</Typography>
          )}
        </Box>

        {/* Status chip */}
        {mode === 'received' ? (
          <Chip
            size="small"
            label={RECEIVED_STATUS[effectiveStatus(inv)]?.label || inv.status}
            color={RECEIVED_STATUS[effectiveStatus(inv)]?.color || 'default'}
            sx={{ ml: 1, fontWeight: 600 }}
          />
        ) : (
          <Chip
            size="small"
            icon={effectiveStatus(inv) === 'pending'
              ? <HourglassEmptyIcon sx={{ fontSize: '0.85rem !important' }} />
              : undefined}
            label={SENT_STATUS[effectiveStatus(inv)]?.label || inv.status}
            sx={{
              ml: 1, fontWeight: 600, fontSize: '0.72rem',
              backgroundColor: SENT_STATUS[effectiveStatus(inv)]?.bg || '#f3f4f6',
              color: SENT_STATUS[effectiveStatus(inv)]?.color || '#6b7280',
              border: `1px solid ${SENT_STATUS[effectiveStatus(inv)]?.color || '#d1d5db'}`,
            }}
          />
        )}
      </Box>

      {/* Details row */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        {mode === 'received' ? (
          <Typography variant="body2" color="text.secondary">
            From <strong>{inv.fromUsername}</strong>
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            To <strong>{inv.toUsername}</strong>
          </Typography>
        )}
        {isCoverTask && inv.taskName && (
          <>
            <Typography variant="body2" color="text.secondary">•</Typography>
            <Chip
              size="small"
              icon={<AssignmentIcon fontSize="inherit" />}
              label={inv.taskName}
              sx={{ fontSize: '0.7rem', height: 22, color: '#D4AF37', borderColor: '#D4AF37' }}
              variant="outlined"
            />
          </>
        )}
        {!isCoverOffer && !isCoverTask && (
          <>
            <Typography variant="body2" color="text.secondary">•</Typography>
            <Chip
              size="small"
              icon={roleConfig.icon}
              label={`Role: ${roleConfig.label}`}
              sx={{ fontSize: '0.7rem', height: 22, color: roleConfig.color, borderColor: roleConfig.color }}
              variant="outlined"
              onClick={e => e.stopPropagation()}
            />
          </>
        )}
        {isCoverOffer && inv.price != null && (
          <>
            <Typography variant="body2" color="text.secondary">•</Typography>
            <Chip
              size="small"
              label={`$${Number(inv.price).toFixed(2)}`}
              sx={{ fontSize: '0.7rem', height: 22, color: '#059669', borderColor: '#059669', fontWeight: 700 }}
              variant="outlined"
            />
          </>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {new Date(inv.createdAt).toLocaleDateString()}
        </Typography>
      </Box>

      {/* Optional message */}
      {inv.message && (
        <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1, px: 1.5, py: 1, borderLeft: '3px solid #D4AF37' }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555' }}>
            "{inv.message}"
          </Typography>
        </Box>
      )}

      {/* Response timestamp */}
      {inv.respondedAt && (
        <Typography variant="caption" color="text.secondary">
          {inv.status === 'accepted' ? 'Accepted' : 'Rejected'} on{' '}
          {new Date(inv.respondedAt).toLocaleDateString()}
        </Typography>
      )}

      {/* Actions (inline — stop propagation so click doesn't open modal) */}
      <Box display="flex" gap={1} mt={0.5} onClick={e => e.stopPropagation()}>
        {mode === 'received' && isPending && !expired && isCoverTask && (
          <>
            <Button
              size="small" variant="contained" color="success"
              startIcon={loading === 'accept' ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
              disabled={!!loading}
              onClick={(e) => act('accept', onAccept, e)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {inv.type === 'manuscript_goal' ? 'Accept Goal' : 'Accept Task'}
            </Button>
            <Button
              size="small" variant="outlined" color="error"
              startIcon={loading === 'reject' ? <CircularProgress size={14} color="inherit" /> : <CancelIcon />}
              disabled={!!loading}
              onClick={(e) => act('reject', onReject, e)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Decline
            </Button>
          </>
        )}
        {mode === 'received' && isPending && !expired && !isCoverOffer && !isCoverTask && (
          <>
            <Button
              size="small" variant="contained" color="success"
              startIcon={loading === 'accept' ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
              disabled={!!loading}
              onClick={(e) => act('accept', onAccept, e)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Accept
            </Button>
            <Button
              size="small" variant="outlined" color="error"
              startIcon={loading === 'reject' ? <CircularProgress size={14} color="inherit" /> : <CancelIcon />}
              disabled={!!loading}
              onClick={(e) => act('reject', onReject, e)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Reject
            </Button>
          </>
        )}
        {mode === 'received' && isPending && !expired && isCoverOffer && (
          <>
            <Button
              size="small" variant="contained"
              startIcon={loading === 'accept' ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
              disabled={!!loading}
              onClick={(e) => act('accept', () => onAcceptCoverOffer(inv), e)}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
            >
              Accept & Assign to Draft
            </Button>
            <Button
              size="small" variant="outlined" color="error"
              startIcon={loading === 'reject' ? <CircularProgress size={14} color="inherit" /> : <CancelIcon />}
              disabled={!!loading}
              onClick={(e) => act('reject', onReject, e)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Reject
            </Button>
          </>
        )}
        {mode === 'sent' && isPending && onCancel && (
          <Button
            size="small" variant="text"
            startIcon={loading === 'cancel' ? <CircularProgress size={14} /> : <CancelIcon fontSize="small" />}
            disabled={!!loading}
            onClick={(e) => act('cancel', onCancel, e)}
            sx={{ textTransform: 'none', color: '#9ca3af', '&:hover': { color: '#ef4444' } }}
          >
            Cancel invitation
          </Button>
        )}
      </Box>
    </ListItem>
  );
}

// ── RequestList ─────────────────────────────────────────────────────────────

export default function RequestList({
  incoming = [],
  outgoing = [],
  onAccept,
  onReject,
  onCancel,
  onAcceptCoverOffer,
  onRefresh,
  loading = false,
}) {
  const [tab, setTab] = useState(0); // 0 = received, 1 = sent
  const [actionError, setActionError] = useState(null);
  const [selectedInv, setSelectedInv] = useState(null);
  const [selectedInvMode, setSelectedInvMode] = useState('received');

  const pendingIncoming = incoming.filter(i => i.status === 'pending' && !isExpired(i.expirationDate)).length;
  const pendingOutgoing = outgoing.filter(i => effectiveStatus(i) === 'pending').length;

  const sort = (arr) => [...arr].sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (b.status === 'pending' && a.status !== 'pending') return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const wrap = (fn) => async (id) => {
    setActionError(null);
    try { await fn(id); }
    catch (e) { setActionError(e.message || 'Action failed'); }
  };

  return (
    <Box className="request-list-root" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Tabs + refresh */}
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} pt={1} pb={0}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36 }}>
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={0.75}>
                Received
                {pendingIncoming > 0 && (
                  <Chip size="small" label={pendingIncoming} color="warning"
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, minWidth: 22 }} />
                )}
              </Box>
            }
            sx={{ minHeight: 36, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }}
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={0.75}>
                Sent
                {pendingOutgoing > 0 && (
                  <Chip size="small" label={pendingOutgoing}
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, minWidth: 22,
                      bgcolor: '#fef3c7', color: '#92400e' }} />
                )}
              </Box>
            }
            sx={{ minHeight: 36, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }}
          />
        </Tabs>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={onRefresh} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      <Divider />

      {actionError && (
        <Box px={2} pt={1}>
          <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>
        </Box>
      )}

      {/* Received tab */}
      {tab === 0 && (
        <List disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
          {incoming.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                No collaboration invitations received yet.
              </Typography>
            </Box>
          ) : sort(incoming).map(inv => (
            <InvitationCard key={inv.id} inv={inv} mode="received"
              onAccept={wrap(onAccept)} onReject={wrap(onReject)}
              onAcceptCoverOffer={onAcceptCoverOffer}
              onClick={() => { setSelectedInv(inv); setSelectedInvMode('received'); }}
            />
          ))}
        </List>
      )}

      {/* Sent tab */}
      {tab === 1 && (
        <List disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
          {outgoing.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                You haven't sent any collaboration invitations yet.
              </Typography>
            </Box>
          ) : sort(outgoing).map(inv => (
            <InvitationCard key={inv.id} inv={inv} mode="sent"
              onCancel={wrap(onCancel)}
              onClick={() => { setSelectedInv(inv); setSelectedInvMode('sent'); }}
            />
          ))}
        </List>
      )}

      {/* Detail modal — received: Accept/Reject shown; sent: view + comment only */}
      <InvitationDetailModal
        inv={selectedInv}
        open={!!selectedInv}
        mode={selectedInvMode}
        onClose={() => setSelectedInv(null)}
        onAccept={wrap(onAccept)}
        onReject={wrap(onReject)}
        onCancel={async (id) => { await wrap(onCancel)(id); setSelectedInv(null); }}
        onUpdate={(updated) => {
          setSelectedInv(updated);
          onRefresh();
        }}
        onCreated={() => {
          setSelectedInv(null);
          onRefresh();
        }}
        onAcceptCoverOffer={(inv) => {
          setSelectedInv(null);
          onAcceptCoverOffer(inv);
        }}
      />
    </Box>
  );
}
